import { useTranslation } from 'react-i18next';
import { Link, useLocation } from '@tanstack/react-router';
import { useService } from '../../hooks/useService.js';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

/** @graph-connects none */
const NAV_ITEMS = [
  { to: '/overview', labelKey: 'nav.overview', icon: '📊' },
  { to: '/screens', labelKey: 'nav.screens', icon: '📱' },
  { to: '/funnels', labelKey: 'nav.funnels', icon: '🔄' },
  { to: '/service-health', labelKey: 'nav.serviceHealth', icon: '🏥' },
  { to: '/users', labelKey: 'nav.users', icon: '👤' },
  { to: '/errors', labelKey: 'nav.errors', icon: '⚠' },
] as const;

/** @graph-connects none */
const STYLING_NAV_ITEMS = [
  { to: '/styling/overview', labelKey: 'nav.stylingOverview', icon: '🎨' },
  { to: '/styling/timing', labelKey: 'nav.stylingTiming', icon: '⏱' },
  { to: '/styling/errors', labelKey: 'nav.stylingErrors', icon: '⚠' },
  { to: '/styling/sku-analysis', labelKey: 'nav.stylingSkuAnalysis', icon: '📦' },
  { to: '/styling/filter-patterns', labelKey: 'nav.stylingFilterPatterns', icon: '🔍' },
  { to: '/styling/stylist-activity', labelKey: 'nav.stylingStylistActivity', icon: '👤' },
] as const;

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business サイドバーナビゲーション。サービス(airCloset/Styling Console)に応じてナビ項目を切替表示
 * @graph-connects useService [calls] 現在のサービス取得フック
 */
export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { service } = useService();
  const navItems = service === 'styling_console' ? STYLING_NAV_ITEMS : NAV_ITEMS;

  return (
    <>
      {/* モバイルオーバーレイ */}
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={onClose} />}

      {/* サイドバー */}
      <aside
        className={`
          fixed md:sticky top-14 left-0 h-[calc(100vh-3.5rem)]
          w-56 glass-sidebar border-r border-[var(--color-border)]
          transform transition-transform duration-200 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        <nav className="p-3 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={`
                  flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${
                    isActive
                      ? 'bg-[var(--color-accent-primary)] text-white'
                      : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]'
                  }
                `}
              >
                <span className="text-base">{item.icon}</span>
                {t(item.labelKey)}
              </Link>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
